import React from 'react';
import 'react-app-polyfill/stable';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { Routes, Route, BrowserRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';
import 'core-js/actual/array/find-last';
import 'core-js/actual/promise/finally';
import { PersistGate } from 'redux-persist/lib/integration/react';
import { createBaseGainsightProvider } from '@rsos/capstone';
import '@rsos/capstone/src/overridePublicPath';
import ErrorBoundary from '@rsos/components/capstone/ErrorBoundary';
import NotFound from '@rsos/components/capstone/ErrorBoundary/NotFound';
import Layout from '@rsos/components/capstone/Layout';
import { FlagProvider } from '@rsos/flags';
import { gspx } from '@rsos/gainsight';
import { configureAuthCallbacks } from '@rsos/reduxDataStore/src/features/rsosApi';
import flattenArray from '@rsos/utils/flattenArray';
import { captureExceptionWithScope } from '@rsos/utils/sentry';
import { forcedLogOut } from './actions/locations';
import App from './app/App';
import { store, persistor } from './store';

// Configure authentication callbacks for handling token refresh failures
const handleSessionExpired = message => {
  // Show toast notification to user
  toast.error(
    message || 'Your session has expired. You will be redirected to login.',
    {
      position: 'top-center',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    },
  );
};

const handleLogout = () => {
  store.dispatch(forcedLogOut());
};

// Set up auth callbacks before any API calls can be made
configureAuthCallbacks(handleLogout, handleSessionExpired);

const dataProvider = createBaseGainsightProvider(store);
// Initialize Gainsight with data provider and error handling
gspx.initialize(dataProvider, {
  captureError: captureExceptionWithScope,
});

const topLevelRoutes = [
  'home',
  'query-interface',
  'irp-home',
  'irp-query-interface',
];

const appPaths = flattenArray(topLevelRoutes.map(route => route));

const AppRoutes = () => (
  <Routes>
    {topLevelRoutes.map(route => (
      <Route key={route} path={route} element={<App />} />
    ))}
    <Route path="*" element={<NotFound />} />
  </Routes>
);

const container = document.getElementById('root');

const root = createRoot(container);

root.render(
  <Provider store={store}>
    <FlagProvider>
      <PersistGate loading={'Loading'} persistor={persistor}>
        <BrowserRouter>
          <Layout appPaths={appPaths} persistor={persistor}>
            <ErrorBoundary>
              <AppRoutes />
            </ErrorBoundary>
          </Layout>
        </BrowserRouter>
      </PersistGate>
    </FlagProvider>
  </Provider>,
);
